import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, ShieldCheck, BadgeCheck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LoadingScreen from "@/components/LoadingScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REDIRECT_URL = "http://a8pay.sbs";
const LOADER_MS = 3000;

export default function MatchPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", age: "" });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Please enter a valid email.";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      return "Please enter a valid phone number.";
    const age = parseInt(form.age, 10);
    if (Number.isNaN(age) || age < 18 || age > 120)
      return "You must be 18 or older to continue.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        age: parseInt(form.age, 10),
      });
    } catch (e) {
      // Non-blocking: still proceed to loading+redirect so user isn't stuck
      // eslint-disable-next-line no-console
      console.error("lead save failed", e);
    } finally {
      setSubmitting(false);
      setLoading(true);
      setTimeout(() => {
        window.location.href = REDIRECT_URL;
      }, LOADER_MS);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <main
      className="min-h-screen w-full bg-white flex items-center justify-center px-4 py-10 sm:py-16"
      data-testid="match-page"
    >
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="flex flex-col items-center text-center mb-8">
          <div
            className="w-14 h-14 rounded-2xl bg-[#FF4B65] flex items-center justify-center mb-4"
            data-testid="brand-logo"
          >
            <Heart className="w-7 h-7 text-white fill-white" />
          </div>
          <h1
            className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#111827]"
            data-testid="page-title"
          >
            Find Your Match
          </h1>
          <p className="mt-2 text-sm sm:text-base text-[#4B5563] max-w-sm">
            Tell us a little about yourself and we'll connect you with real
            singles near you.
          </p>
        </div>

        {/* Form card */}
        <div
          className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm"
          data-testid="form-card"
        >
          <form onSubmit={onSubmit} className="space-y-4" data-testid="match-form" noValidate>
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-[#111827]">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Jane Doe"
                autoComplete="name"
                value={form.name}
                onChange={update("name")}
                className="mt-1.5 h-11 rounded-lg text-base"
                data-testid="input-name"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#111827]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@email.com"
                autoComplete="email"
                value={form.email}
                onChange={update("email")}
                className="mt-1.5 h-11 rounded-lg text-base"
                data-testid="input-email"
                required
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-sm font-medium text-[#111827]">
                Phone
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(555) 123-4567"
                autoComplete="tel"
                inputMode="tel"
                value={form.phone}
                onChange={update("phone")}
                className="mt-1.5 h-11 rounded-lg text-base"
                data-testid="input-phone"
                required
              />
            </div>

            <div>
              <Label htmlFor="age" className="text-sm font-medium text-[#111827]">
                Age
              </Label>
              <Input
                id="age"
                type="number"
                min="18"
                max="120"
                placeholder="27"
                inputMode="numeric"
                value={form.age}
                onChange={update("age")}
                className="mt-1.5 h-11 rounded-lg text-base"
                data-testid="input-age"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-lg bg-[#FF4B65] hover:bg-[#E63950] text-white font-semibold text-base"
              data-testid="submit-button"
            >
              {submitting ? "Please wait..." : "Find My Match"}
            </Button>

            <div
              className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-[#4B5563] pt-2"
              data-testid="trust-badges"
            >
              <span className="inline-flex items-center gap-1">
                <BadgeCheck className="w-3.5 h-3.5 text-[#FF4B65]" />
                Free to join
              </span>
              <span className="inline-flex items-center gap-1">
                <Lock className="w-3.5 h-3.5 text-[#FF4B65]" />
                Secure
              </span>
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-[#FF4B65]" />
                18+ only
              </span>
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-[#6B7280]" data-testid="legal-note">
          By continuing you agree to our Terms and Privacy Policy.
        </p>
      </div>
    </main>
  );
}
